import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Typography } from '@mui/material';
import { Base } from 'components/Containers/BaseContainer';
import SquaresDashboardHandler from 'handlers/squaresHandlers/squaresDashboard.handler';
import useAppTheme from 'hooks/useAppTheme';
import { ThemePalette } from 'mui.theme';
import { useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import { setSquaresDashboard } from 'store/SquaresStore/DashboardSquaresState.slice';
import { capitalizeFirstLetter } from 'utils/helpers/string.manipulation';
import LoadingDots from 'views/FleetOverview/components/AssetCardsV2/LoadingDots';
import { ComponentsLineChart } from './Components/LineChart';
import { ComponentsPieChart } from './Components/PieChart';
import { Subcomponent } from './Components/Subcomponent';
import { ComponentsTreeChart } from './Components/TreeChart';
import {
  type CategoryData,
  extractSelectedAssets,
  extractWorkOrderData,
  getAssetsByKey,
  getFirstAttribute,
  getMaxAssetAndIndexForComponent,
} from './utils';
import CustomDateRangePicker from 'components/DatePIcker/CustomDateRangePicker';
import dayjs from 'dayjs';
import { filterWorkOrdersByName } from 'utils/workorders';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const FilterSection = ({
  setSelectedCell,
  setIsLoading,
  isLoading,
  setComponentType,
}: {
  setSelectedCell: (param: string) => void;
  setIsLoading: (param: boolean) => void;
  setComponentType: (param: string) => void;
  isLoading: boolean;
}) => {
  const { t } = useTranslation();
  const squaresHandler = new SquaresDashboardHandler();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const accessToken = useAppSelector((state) => state.authReducer.accessToken);
  const dispatch = useAppDispatch();

  // state
  const formerSelectedDates: any = sessionStorage.getItem(
    'datePickerSelection'
  );
  let initialStartDate = new Date();
  initialStartDate.setFullYear(initialStartDate.getFullYear() - 1);
  let initialEndDate = new Date();
  if (formerSelectedDates) {
    const dateObjs = JSON.parse(formerSelectedDates);
    initialStartDate = new Date(dateObjs.startDate);
    initialEndDate = new Date(dateObjs.endDate);
  }
  const [selectedStartDate, setStartDate] = useState(initialStartDate);
  const [selectedEndDate, setEndDate] = useState(initialEndDate);

  const handleDateChange = (range: any) => {
    const [startDate, endDate] = range;
    setStartDate(startDate);
    setEndDate(endDate);

    sessionStorage.setItem(
      'datePickerSelection',
      JSON.stringify({
        startDate,
        endDate,
      })
    );
  };

  const fetchData = async () => {
    try {
      if (selectedStartDate && selectedEndDate) {
        setIsLoading(true);
        setSelectedCell('');
        setComponentType('component');
        // to clear previous data
        dispatch(
          setSquaresDashboard({
            assets: {},
            components: {},
            timeseries: {},
            assetComponentsCosts: {},
            workOrders: [],
          })
        );

        const responseComponentsData =
          await squaresHandler.getPartsComponentsData(
            selectedStartDate,
            selectedEndDate
          );

        // setting highest key on data load
        const highestKey = getFirstAttribute(
          responseComponentsData?.components
        );
        setSelectedCell(highestKey);
      } else {
        dispatch(
          showNotification({
            message: t('please_select_a_valid_date_range'),
            type: 'error',
          })
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      void fetchData();
    }
  }, [accessToken]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        sx={{
          textAlign: 'center',
          fontSize: '20px',
          color: palette.textPrimary,
        }}
      >
        {t('parts_work_order_cost_totals')}
      </Typography>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '8px',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '12px',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              height: '30px',
              display: 'flex',
              flexDirection: 'row',
              gap: '2px',
              alignItems: 'center',
            }}
          >
            <p
              style={{
                color: palette.textPrimary,
              }}
            >
              {t('select_date_range_2')}
            </p>
            <CustomDateRangePicker
              onFinalDateChange={handleDateChange}
              startDate={selectedStartDate}
              endDate={selectedEndDate}
              maxDate={dayjs(new Date().getTime())}
              views={['year', 'month', 'day']}
              autoOpenEnabled={false}
            />
          </div>

          <Button
            sx={{
              color: '#FFFFFF',
              border: '1px solid #2F445E',
              backgroundColor: palette.primary,
              fontSize: '12px',
              textTransform: 'none',
              height: '30px',
              '&:hover': {
                color: enableDarkTheme ? 'white' : 'black',
                border: enableDarkTheme ? '1px solid white' : '1px solid black',
              },
            }}
            disabled={isLoading}
            onClick={fetchData}
          >
            {t('apply')}
          </Button>
        </div>
      </div>
    </div>
  );
};

const TreeChartSection = ({
  selectedCell,
  workOrderData,
  setSelectedCell,
  setComponentType,
}: {
  selectedCell: string;
  workOrderData: CategoryData[];
  setSelectedCell: (param: string) => void;
  setComponentType: (param: string) => void;
}) => {
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTreeCellClick = (event: any, name: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedCell(name);
  };

  const handleShowTab = (type: string, name: string) => {
    setComponentType(type);
  };

  return (
    <Box>
      <ComponentsTreeChart
        selectedCell={selectedCell}
        handleCellClick={handleTreeCellClick}
        data={workOrderData}
        enableDarkTheme={enableDarkTheme}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        handleClose={handleClose}
        showPopOver={true}
        handleShowTab={handleShowTab}
      />
    </Box>
  );
};
const SubcomponentSection = ({ selectedCell }: { selectedCell: string }) => {
  const { dashboard } = useAppSelector(
    (state) => state.squaresDashboardReducer
  );

  const selectedSubCompData = useMemo(() => {
    const formattedData = dashboard?.workOrders?.map((item) => {
      return {
        ...item,
        sub_component_categories: item?.sub_component_categories?.replace(
          /\[|\]/g,
          ''
        ),
      };
    });
    const tempSelectedSubCompData = formattedData?.filter(
      (item) => item?.components_category === selectedCell
    );
    return tempSelectedSubCompData;
  }, [dashboard?.workOrders, selectedCell]);

  return <Subcomponent selectedCellData={selectedSubCompData} />;
};

const DistributionSection = ({
  selectedCell,
  workOrderData,
}: {
  selectedCell: string;
  workOrderData: CategoryData[];
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const { dashboard: data } = useAppSelector(
    (state) => state.squaresDashboardReducer
  );

  const [isLoading, setIsLoading] = useState(true);
  const [selectedAssetsData, setSelectedAssetsData] = useState<{
    assets: string[];
    index: number[];
  }>({
    assets: [],
    index: [],
  });

  const handlePieClick = (data: any, index: number) => {
    const name = data?.name;

    if (name) {
      setSelectedAssetsData(() => ({
        assets: [name],
        index: [index],
      }));
    }
  };

  const pieChartData = useMemo(() => {
    const temp = getAssetsByKey(data?.components ?? {}, selectedCell);
    return temp;
  }, [data?.components, selectedCell]);

  const selectedCellTotalCost = useMemo(() => {
    const selectedComponent = workOrderData.find(
      (item) => item.name === selectedCell
    );
    return selectedComponent?.totalCost ?? 0;
  }, [workOrderData, selectedCell]);

  console.log(selectedCellTotalCost);

  useEffect(() => {
    if (!pieChartData || pieChartData.length === 0) return;

    const maxAsset = getMaxAssetAndIndexForComponent(
      pieChartData,
      'total_asset_cost'
    );

    if (maxAsset?.maxData?.name) {
      setSelectedAssetsData({
        assets: [maxAsset.maxData.name],
        index: [maxAsset.maxIndex],
      });
    }

    setIsLoading(false);
  }, [pieChartData]);

  const lineChartData = useMemo(() => {
    const temp = extractSelectedAssets(
      data.assetComponentsCosts,
      selectedAssetsData?.assets,
      selectedCell
    );
    return temp;
  }, [data?.assetComponentsCosts, selectedAssetsData?.assets, selectedCell]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="400px"
        width={'100%'}
      >
        <Typography
          sx={{
            fontSize: '20px',
            color: palette.textPrimary,
          }}
        >
          {t('cdp_loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)', // 5 equal-width columns
        gap: '8px',
        padding: '20px',
        width: '100%',
        boxSizing: 'border-box',
        borderRadius: '8px',
      }}
    >
      {selectedCellTotalCost === 0 ? (
        <Typography
          sx={{
            textAlign: 'center',
            fontSize: '20px',
            paddingTop: '10px',
            color: palette.textPrimary,
          }}
        >
          {t('no_cost_associated_for_the_component_selected')}
        </Typography>
      ) : (
        <>
          <div style={{ gridColumn: 'span 2', width: '100%' }}>
            <Base
              sx={{
                height: '100%',
                maxHeight: '100%',
                backgroundColor: enableDarkTheme
                  ? palette.boxBackground
                  : palette.boxBackground,
                margin: 0,
              }}
            >
              <Box sx={{ textAlign: 'center' }} paddingTop={2}>
                <Typography
                  sx={{
                    color: palette.textPrimary,
                  }}
                >
                  {t('asset_distribution_by_total_cost_for')}
                </Typography>
                <Typography color={'#8884d8'} fontWeight={600}>
                  {selectedCell}
                </Typography>
              </Box>
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'center',
                  height: '400px',
                }}
              >
                <ComponentsPieChart
                  data={pieChartData}
                  handlePieClick={handlePieClick}
                  enableDarkTheme={enableDarkTheme}
                  onlyOneActive
                  activeIndices={selectedAssetsData?.index}
                />
              </Box>
            </Base>
          </div>
          <div style={{ gridColumn: 'span 3', width: '100%' }}>
            <Base
              sx={{
                height: '100%',
                maxHeight: '100%',
                backgroundColor: enableDarkTheme
                  ? palette.boxBackground
                  : palette.boxBackground,
                margin: 0,
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                paddingTop={2}
              >
                <Typography
                  sx={{
                    color: palette.textPrimary,
                  }}
                >
                  {t('total_cost_distribution_for_nbsp')}
                </Typography>
                <Typography color="#8884d8" fontWeight={600}>
                  {selectedCell}
                </Typography>
                <Typography
                  sx={{
                    color: palette.textPrimary,
                  }}
                >
                  {t('nbsp_of_asset_nbsp')}
                </Typography>
                <Typography color="#8884d8" fontWeight={600}>
                  {selectedAssetsData?.assets?.join(',')}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {lineChartData.length < 1 ? (
                  <Typography
                    sx={{
                      textAlign: 'center',
                      fontSize: '20px',
                      paddingTop: '10px',
                      color: palette.textPrimary,
                    }}
                  >
                    {t('please_select_an_asset_from_the_pie_chart')}
                  </Typography>
                ) : (
                  <Box sx={{ width: '100%', height: '420px' }}>
                    <ComponentsLineChart data={lineChartData} height="100%" />
                  </Box>
                )}
              </Box>
            </Base>
          </div>
        </>
      )}
    </div>
  );
};

export const PartsViewV2 = ({
  showBackButton,
  setTabIndex,
}: {
  showBackButton?: boolean;
  setTabIndex?: any;
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { dashboard: data } = useAppSelector(
    (state) => state.squaresDashboardReducer
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [componentType, setComponentType] = useState('component');
  const [selectedCell, setSelectedCell] = useState<string>('');

  // memoized vars
  const workOrderData = useMemo(() => {
    const tempWorkOrderData = extractWorkOrderData(data?.components ?? {});
    // min non-zero totalCost
    const minNonZeroValue = Math.min(
      ...tempWorkOrderData.map((el) =>
        el?.totalCost > 0 ? el?.totalCost : Infinity
      )
    );

    // fallback to zero only if all zero else get a value less than min but greater than 0
    const minDisplayValue =
      minNonZeroValue === Infinity ? 0 : Math.ceil(minNonZeroValue / 10);

    const formattedWorkOrderData = tempWorkOrderData.map((el) => ({
      ...el,
      totalCostForDisplay:
        el?.totalCost === 0 ? minDisplayValue : el?.totalCost,
    }));

    return filterWorkOrdersByName(formattedWorkOrderData);
  }, [data?.components]);

  return (
    <Box>
      {showBackButton && (
        <Button
          sx={{
            textTransform: 'none',
            border: '1px solid #3666EC',
            color: enableDarkTheme
              ? ThemePalette.typography.wheat
              : ThemePalette.typography.black,
          }}
          onClick={() => {
            setTabIndex(0);
          }}
        >
          {t('cdp_button_back')}
        </Button>
      )}

      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <FilterSection
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          setSelectedCell={setSelectedCell}
          setComponentType={setComponentType}
        />
        {isLoading ? (
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: '16px',
              paddingTop: '10px',
              color: palette.textPrimary,
            }}
          >
            {t('loading_parts_please_wait')}
            <LoadingDots enableDarkTheme={enableDarkTheme} />{' '}
          </Typography>
        ) : (
          <>
            {workOrderData.length <= 0 ? (
              <Typography
                sx={{
                  textAlign: 'center',
                  fontSize: '20px',
                  paddingTop: '10px',
                  color: palette.textPrimary,
                }}
              >
                {' '}
                {t('no_data_please_select_a_different_date_range')}
              </Typography>
            ) : (
              <>
                {componentType === 'component' ? (
                  <>
                    <Base
                      sx={{
                        padding: '20px',
                        backgroundColor: enableDarkTheme
                          ? palette.toolBarBackground
                          : palette.toolBarBackground,
                        margin: 0,
                      }}
                    >
                      <TreeChartSection
                        selectedCell={selectedCell}
                        setComponentType={setComponentType}
                        setSelectedCell={setSelectedCell}
                        workOrderData={workOrderData}
                      />
                    </Base>

                    <Box
                      sx={{
                        backgroundColor: enableDarkTheme
                          ? palette.toolBarBackground
                          : palette.toolBarBackground,
                      }}
                    >
                      {selectedCell ? (
                        <DistributionSection
                          selectedCell={selectedCell}
                          workOrderData={workOrderData}
                        />
                      ) : (
                        <Typography
                          sx={{
                            textAlign: 'center',
                            fontSize: '20px',
                            paddingTop: '10px',
                            color: palette.textPrimary,
                          }}
                        >
                          {t('please_select_a_part_to_see_distribution')}
                        </Typography>
                      )}
                    </Box>
                  </>
                ) : (
                  <>
                    <Box display={'flex'} alignItems={'center'} gap={'5px'}>
                      <ArrowBackIcon
                        sx={{
                          color: palette.textPrimary,
                          fontSize: '20px',
                        }}
                        onClick={() => {
                          setComponentType('component');
                        }}
                      />
                      <Typography
                        sx={{
                          color: palette.textPrimary,
                          fontSize: '20px',
                        }}
                      >
                        {capitalizeFirstLetter(selectedCell)}
                      </Typography>
                    </Box>
                    <SubcomponentSection selectedCell={selectedCell} />
                  </>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
